// React
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

//Constants
import { TIMEZONE } from '../../data/valueConstants';

// Components
import Dropdown from '../../msreact-kit/html-components/drop-down';
import Pagination from '../../msreact-kit/html-components/table/pagination';
import { dropDownData } from '../../msreact-kit/html-components/table/data';
import CustomToolTip from '../Shared/custom-tooltip';

// Data
import { COLUMNS } from './data';

// Css
import './index.scss';

const statusClassMap = {
  ACTIVE: 'discount-grid__status--active',
  EXPIRED: 'discount-grid__status--expired',
  INACTIVE: 'discount-grid__status--deactivated',
  COMPLETED: 'discount-grid__status--used'
};

const StatusBadge = ({ status, statusLabels }) => {
  const labels = {
    ACTIVE: statusLabels?.activated,
    EXPIRED: statusLabels?.expired,
    INACTIVE: statusLabels?.deactivated,
    COMPLETED: statusLabels?.used
  };
  return (
    <span className={`discount-grid__status ${statusClassMap[status] || ''}`}>
      {labels[status] || status}
    </span>
  );
};

const formatDateIST = (dateStr) => {
  if (!dateStr) return '-';
  return moment(dateStr).utcOffset(TIMEZONE.IN).format('DD/MM/YYYY, hh:mm A');
};
const ActionCell = ({ row, onAction, tableContent, status }) => (
  <div className='discount-grid__actions-cell'>
    <button
      className='discount-grid__actions-cell__btn discount-grid__actions-cell__btn--view'
      title={tableContent?.action_view}
      onClick={() => onAction('view', row)}
    >
      <i className='a-icon boschicon-bosch-ic-watch-on' />
    </button>
    <button
      className={`discount-grid__actions-cell__btn discount-grid__actions-cell__btn--delete 
    ${status === 'EXPIRED' || status === 'COMPLETED' ? 'discount-grid__actions-cell__btn--disabled' : ''}`}
      title={tableContent?.action_edit}
      onClick={() => onAction('edit', row)}
      disabled={status === 'EXPIRED' || status === 'COMPLETED'}
    >
      <i className='a-icon boschicon-bosch-ic-edit' />
    </button>
    <button
      className={`discount-grid__actions-cell__btn discount-grid__actions-cell__btn--delete 
    ${status === 'EXPIRED' || status === 'COMPLETED' ? 'discount-grid__actions-cell__btn--disabled' : ''}`}
      title={tableContent?.action_delete}
      onClick={() => onAction('delete', row)}
      disabled={status === 'EXPIRED' || status === 'COMPLETED'}
    >
      <i className='a-icon boschicon-bosch-ic-delete' />
    </button>
  </div>
);

const TruncatedCell = ({ id, value }) => (
  <CustomToolTip
    id={id}
    tooltipData={value}
    width='dynamic'
    className='discount-grid__cell-tooltip'
  >
    <div className='text-ellipsis' id={id}>
      {value}
    </div>
  </CustomToolTip>
);

TruncatedCell.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string
};

const DiscountGrid = memo(
  ({
    data = [],
    onAction,
    tableContent,
    statusLabels,
    currentPage,
    pageSize,
    totalPages,
    onPageChange
  }) => {
    const TD = 'discount-grid__table__td';
    const TD_TRUNC = `${TD} ${TD}--ellipsis`;

    const handlePerPage = (event) => {
      onPageChange(0, Number(event.value.value));
    };

    const columns = COLUMNS.map((col) => ({
      key: col.key,
      label: tableContent?.columns?.[col.key] || ''
    }));

    return (
      <div>
        <div className='discount-grid__table-wrapper'>
          <table className='discount-grid__table'>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className='discount-grid__table__th'>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr className='discount-grid__table__empty-row'>
                  <td
                    colSpan={columns.length}
                    className='discount-grid__table__empty'
                  >
                    {tableContent?.empty}
                  </td>
                </tr>
              ) : (
                data.map((row) => (
                  <tr key={row.code} className='discount-grid__table__row'>
                    <td className={TD}>
                      <span className='discount-grid__coupon-code'>{row.code}</span>
                    </td>
                    <td className={TD_TRUNC}>
                      <TruncatedCell id={`td-${row.code}-name`} value={row.name} />
                    </td>
                    <td className={TD_TRUNC}>
                      <TruncatedCell
                        id={`td-${row.code}-product`}
                        value={row.products?.[0]?.productName}
                      />
                    </td>
                    <td className={TD_TRUNC}>
                      <TruncatedCell
                        id={`td-${row.code}-start`}
                        value={formatDateIST(row.startDate)}
                      />
                    </td>
                    <td className={TD_TRUNC}>
                      <TruncatedCell
                        id={`td-${row.code}-end`}
                        value={formatDateIST(row.endDate)}
                      />
                    </td>
                    <td className={TD}>
                      <StatusBadge
                        status={row.couponStatus}
                        statusLabels={statusLabels}
                      />
                    </td>
                    <td className={TD_TRUNC}>
                      <TruncatedCell
                        id={`td-${row.code}-order`}
                        value={row.orders?.[0]?.productOrderId || '-'}
                      />
                    </td>
                    <td className={TD}>
                      <ActionCell
                        row={row}
                        onAction={onAction}
                        tableContent={tableContent}
                        status={row.couponStatus}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className='discount-grid__pagination-bar'>
          <div className='discount-grid__pagination-bar__items'>
            <span className='discount-grid__pagination-bar__items__title'>
              {tableContent?.items_per_page} :
            </span>
            <div className='discount-grid__pagination-bar__items__drop'>
              <Dropdown
                {...dropDownData}
                handleChange={handlePerPage}
                value={pageSize}
              />
            </div>
          </div>
          {totalPages > 0 && (
            <div className='discount-grid__pagination-bar__pagination'>
              <Pagination
                className='pagination-bar'
                currentPage={currentPage}
                paginationData={{ totalPages }}
                onPageChange={(page) => onPageChange(page, pageSize)}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  statusLabels: PropTypes.object
};

ActionCell.propTypes = {
  row: PropTypes.object.isRequired,
  onAction: PropTypes.func.isRequired,
  tableContent: PropTypes.object
};

DiscountGrid.propTypes = {
  data: PropTypes.array,
  onAction: PropTypes.func.isRequired,
  tableContent: PropTypes.object,
  statusLabels: PropTypes.object,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func.isRequired
};

DiscountGrid.defaultProps = { data: [] };

export default DiscountGrid;
